import api from './api';

export const authService = {
  // Login - Step 1
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    
    console.log('🔍 authService.login - Full response:', response);
    console.log('🔍 authService.login - Response data:', response.data);
    
    // If 2FA is required, don't store token yet
    if (response.data.data.requiresTwoFactor) {
      return response;
    }
    
    // Extract token and user from the correct nested structure
    // Backend returns: { success, data: { token, user }, message, timestamp }
    const token = response.data?.data?.token || response.data?.token;
    const user = response.data?.data?.user || response.data?.user;
    
    console.log('🔍 authService.login - Extracted token:', token ? 'Present' : 'Missing');
    console.log('🔍 authService.login - Extracted user:', user);
    
    // If no 2FA required, store token and user
    if (token) {
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      console.log('🔍 authService.login - Token and user stored in localStorage');
    }
    return response;
  },

  // Send 2FA code
  send2FACode: async (data) => {
    const response = await api.post('/auth/2fa/send', data);
    return response;
  },

  // Verify 2FA code
  verify2FA: async (data) => {
    const response = await api.post('/auth/2fa/verify', data);
    
    // Store token and user after successful 2FA with cache busting
    if (response.data.token) {
      // Clear any existing auth data first
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Set new auth data
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      console.log('🔍 AuthService - 2FA token and user stored successfully');
    }
    return response;
  },

  // Logout
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  },

  // Get current user
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  // Get user profile
  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  // Get agent profile with detailed information
  getAgentProfile: async () => {
    const response = await api.get('/agents/profile');
    return response.data;
  },

  // Register agency agent
  registerAgencyAgent: async (data) => {
    const response = await api.post('/registration/agency', data);
    return response.data;
  },

  // Register independent agent
  registerIndependent: async (data) => {
    const response = await api.post('/registration/independent', data);
    return response.data;
  },

  // Request agent password reset
  requestAgentPasswordReset: async (data) => {
    const response = await api.post('/auth/agent/password-reset/request', data);
    return response.data;
  },

  // Confirm agent password reset
  confirmAgentPasswordReset: async (data) => {
    const response = await api.post('/auth/agent/password-reset/confirm', data);
    return response.data;
  },

  // Choose 2FA method for password reset
  choosePasswordReset2FAMethod: async (data) => {
    const response = await api.post('/auth/agent/password-reset/choose-method', data);
    return response.data;
  },

  // Send 2FA code for password reset
  sendPasswordReset2FA: async (data) => {
    const response = await api.post('/auth/agent/password-reset/send-2fa', data);
    return response.data;
  },

  // Website-based password reset with immediate 2FA
  websitePasswordReset: async (data) => {
    const response = await api.post('/auth/agent/password-reset/website', data);
    return response.data;
  },

  // Verify 2FA for password reset
  verifyPasswordReset2FA: async (data) => {
    const response = await api.post('/auth/agent/password-reset/verify-2fa', data);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  // Request admin password reset
  requestAdminPasswordReset: async (data) => {
    const response = await api.post('/auth/admin/password-reset/request', data);
    return response.data;
  },

  // Confirm admin password reset
  confirmAdminPasswordReset: async (data) => {
    const response = await api.post('/auth/admin/password-reset/confirm', data);
    return response.data;
  },

  // Request payment access (sends 2FA code)
  requestPaymentAccess: async (paymentId) => {
    const response = await api.post(`/payments/${paymentId}/request-access`);
    return response.data;
  },

  // Verify payment access code
  verifyPaymentAccess: async (paymentId, code) => {
    const response = await api.post('/auth/verify-payment-access', { paymentId, code });
    return response;
  },

  // Request group payment access (sends 2FA code)
  requestGroupPaymentAccess: async (participantId, method = 'email') => {
    const response = await api.post('/auth/request-group-payment-access', { participantId, method });
    return response.data;
  },

  // Get group payment details with verification code
  getGroupPaymentDetails: async (participantId, code) => {
    const response = await api.post('/auth/group-payment-details', { participantId, code });
    return response.data;
  },

  // Independent Agent 2FA methods
  sendIndependent2FA: async (tempToken, method) => {
    const response = await api.post('/auth/independent/2fa/send', { tempToken, method });
    return response.data;
  },

  verifyIndependent2FA: async (tempToken, code) => {
    const response = await api.post('/auth/independent/2fa/verify', { tempToken, code });
    
    // Store token and user after successful 2FA
    if (response.data.token) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      console.log('🔍 AuthService - Independent Agent 2FA token and user stored successfully');
    }
    return response.data;
  },
};